import { TUNING, type Game, type GameSnapshot } from "../core/game";

/**
 * The overlay: score, lives, slop mode and the between-round banners.
 *
 * Written straight to the DOM rather than into the canvas, so Playwright can
 * assert on real text instead of pixel-peeping a WebGL buffer.
 */

const MODE_LABEL = {
  scatter: "SLOP: PATROLLING",
  chase: "SLOP: HUNTING",
  frightened: "SLOP: OUT OF QUOTA",
} as const;

export class Hud {
  private readonly score: HTMLElement;
  private readonly points: HTMLElement;
  private readonly lives: HTMLElement;
  private readonly mode: HTMLElement;
  private readonly banner: HTMLElement;
  private readonly progress: HTMLElement;
  private readonly quotaTimer: HTMLElement;
  private readonly quotaBar: HTMLElement;

  constructor(private readonly root: HTMLElement) {
    this.root.innerHTML = `
      <div class="hud-row hud-top">
        <div class="hud-stat"><span class="hud-label">SCORE</span><span class="hud-value" data-testid="score">0</span></div>
        <div class="hud-stat"><span class="hud-label">STORY POINTS LEFT</span><span class="hud-value" data-testid="points-left">0</span></div>
        <div class="hud-stat"><span class="hud-label">LIVES</span><span class="hud-value" data-testid="lives">3</span></div>
      </div>
      <div class="hud-progress"><i data-testid="progress"></i></div>
      <div class="hud-mode" data-testid="mode">SLOP: PATROLLING</div>
      <div class="quota-timer" data-testid="quota-timer" hidden>
        <span class="quota-label">5H QUOTA RESET</span>
        <div class="quota-bar"><i data-testid="quota-bar"></i></div>
      </div>
      <div class="hud-banner" data-testid="banner"></div>
    `;
    this.score = this.require("score");
    this.points = this.require("points-left");
    this.lives = this.require("lives");
    this.mode = this.require("mode");
    this.banner = this.require("banner");
    this.progress = this.require("progress");
    this.quotaTimer = this.require("quota-timer");
    this.quotaBar = this.require("quota-bar");
  }

  private require(testid: string): HTMLElement {
    const el = this.root.querySelector<HTMLElement>(`[data-testid="${testid}"]`);
    if (el === null) throw new Error(`HUD is missing [data-testid="${testid}"]`);
    return el;
  }

  update(snap: GameSnapshot): void {
    this.score.textContent = String(snap.score);
    this.points.textContent = String(snap.storyPointsLeft);
    this.lives.textContent = String(Math.max(0, snap.lives));
    this.mode.textContent = MODE_LABEL[snap.mode];
    this.mode.dataset["mode"] = snap.mode;

    const done = snap.storyPointsTotal - snap.storyPointsLeft;
    this.progress.style.width = `${(done / Math.max(1, snap.storyPointsTotal)) * 100}%`;

    // The 5h Quota Reset countdown: the player needs to know how long they have
    // left to keep hunting, not just that the window is open.
    const active = snap.frightenedRemaining > 0;
    this.quotaTimer.hidden = !active;
    if (active) {
      const fraction = Math.max(0, Math.min(1, snap.frightenedRemaining / TUNING.frightenedSeconds));
      this.quotaBar.style.width = `${fraction * 100}%`;
      this.quotaTimer.dataset["expiring"] =
        snap.frightenedRemaining <= TUNING.frightenedWarningSeconds ? "true" : "false";
      this.quotaTimer.dataset["seconds"] = snap.frightenedRemaining.toFixed(1);
    }

    this.banner.textContent = bannerFor(snap);
    this.banner.dataset["status"] = snap.status;
  }
}

export const bannerFor = (snap: GameSnapshot): string => {
  switch (snap.status) {
    case "ready":
      return "STAND BY";
    case "dying":
      return "SLOPPED";
    case "levelComplete":
      return `DECK CLEARED — ${snap.score}`;
    case "gameOver":
      return "GAME OVER";
    case "playing":
      return "";
  }
};

/** Keyboard steering. Returns a teardown so tests can unbind cleanly. */
export const bindControls = (game: Game, target: Window = window): (() => void) => {
  const KEYS: Readonly<Record<string, "up" | "down" | "left" | "right">> = {
    ArrowUp: "up",
    ArrowDown: "down",
    ArrowLeft: "left",
    ArrowRight: "right",
    KeyW: "up",
    KeyS: "down",
    KeyA: "left",
    KeyD: "right",
  };
  const onKey = (event: KeyboardEvent): void => {
    const dir = KEYS[event.code];
    if (dir === undefined) return;
    event.preventDefault();
    game.steer(dir);
  };
  target.addEventListener("keydown", onKey);
  return () => target.removeEventListener("keydown", onKey);
};
